import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMatch } from 'react-router-dom';

import { AuthNewLocalStorage } from '@pkg/components/Web3Auth/enums';
import { useAppDispatch, useAppSelector, useUpdateUser } from '@pkg/hooks';

import { TwitterConnectLocalStorage } from '@app/pages/GetStarted/TwitterConnect/types/enums';
import { PRIMARY_YAT_SELECTION, TWITTER_CONNECT } from '@app/pages/GetStarted/routes';
import { InviteFlowLocalStorage } from '@app/pages/Groups/invites/types/enums';
import { PathParams } from '@app/routes/types';
import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

const SKIP_ONBOARDING = true;
export function useFlowChecks() {
    const dispatch = useAppDispatch();
    const { mutate: updateUser } = useUpdateUser();

    const [tnyRedirectRequired, setTnyRedirectRequired] = useState(false);

    const disabledTwitterConnectFlow = useAppSelector(
        (state) => state.featureFlags.features.disabledTwitterConnectFlow,
    );

    const userYatStatus = useAppSelector((state) => state.user.userAccount?.user.eid_status);
    const isAuthenticated = useAuthTokens((state) => state.isAuthenticated);

    const hasPassword = useAuthTokens((s) => s.hasPassword);

    const isDraftUser = useAppSelector((state) => state.auth.isDraftUser);
    const twitterData = useAppSelector((state) => state.user.userAccount?.twitter_data);
    const currentEmoji = useAppSelector((state) => state.userEmojis.currentEmoji);
    const userEmojis = useAppSelector((state) => state.userEmojis.emojis);
    const user = useAppSelector((state) => state.user);
    const userPendingIncoming = useAppSelector((state) => state.transfers.userPendingIncoming);

    const isInviteFlow = localStorage.getItem(InviteFlowLocalStorage.UserStartedInviteFlow) === 'true';
    const isNewAuth = !!parseInt(localStorage.getItem(AuthNewLocalStorage.OAuthLoggedIn) || '0');
    const newUserTwitterConnected = !!parseInt(
        localStorage.getItem(TwitterConnectLocalStorage.UserNYTwitterConnected) || '0',
    );
    const hasSkippedTwitter = localStorage.getItem(TwitterConnectLocalStorage.UserSkippedTwitter) === 'true';
    const hasChangedTwitter = localStorage.getItem(TwitterConnectLocalStorage.UserChangeTwitter) === 'true';
    const hasTwitter = !hasChangedTwitter && Boolean(twitterData);
    const skipTwitter = hasSkippedTwitter || disabledTwitterConnectFlow || newUserTwitterConnected;

    const passwordRequired = useMemo(
        () => isAuthenticated && !hasPassword && !isNewAuth && !isDraftUser,
        [hasPassword, isAuthenticated, isDraftUser, isNewAuth],
    );

    const userConfirmedYat = userYatStatus === 'Confirmed';

    const isTwitterRoute = useMatch(`/${TWITTER_CONNECT}`);
    const isPYSRoute = useMatch(`/${PRIMARY_YAT_SELECTION}`);
    const isTransferRoute = useMatch({ path: '/transfer' });
    const isOnboardingRoute = Boolean(isTwitterRoute || isPYSRoute);
    const isSuccessRoute = Boolean(
        useMatch({
            path: `/:${[PathParams.Yat]}/success`,
        }),
    );

    const handleConfirmPrimaryYat = useCallback(() => {
        const primaryYat = user?.primaryYat || currentEmoji;
        updateUser({
            value: {
                eid: primaryYat,
                confirm_eid: true,
            },
            dispatch,
        });
    }, [currentEmoji, dispatch, updateUser, user?.primaryYat]);

    const isFirstPurchase = useMemo(() => Boolean(userEmojis?.length === 1), [userEmojis?.length]);

    useEffect(() => {
        const userHasYats = userEmojis?.length > 0;
        setTnyRedirectRequired(Boolean(!isInviteFlow && !isOnboardingRoute && userHasYats));
    }, [isInviteFlow, isOnboardingRoute, userEmojis?.length]);

    const transferRedirect = useMemo(
        () => Boolean(isTransferRoute && !userPendingIncoming),
        [isTransferRoute, userPendingIncoming],
    );

    const primaryYatRequired = useMemo(
        () => Boolean(tnyRedirectRequired && !userConfirmedYat),
        [tnyRedirectRequired, userConfirmedYat],
    );

    useEffect(() => {
        const userHasYats = userEmojis?.length > 0;
        if (SKIP_ONBOARDING && !userConfirmedYat && userHasYats) {
            handleConfirmPrimaryYat();
        } else {
            if (primaryYatRequired && isFirstPurchase && isSuccessRoute) {
                return;
            }
        }
    }, [
        handleConfirmPrimaryYat,
        isFirstPurchase,
        isSuccessRoute,
        primaryYatRequired,
        userConfirmedYat,
        userEmojis?.length,
    ]);

    const twitterRedirect = useMemo(() => {
        if (!SKIP_ONBOARDING) {
            return tnyRedirectRequired && userConfirmedYat && !skipTwitter && !hasTwitter;
        }
        return false;
    }, [hasTwitter, skipTwitter, tnyRedirectRequired, userConfirmedYat]);

    //const postLoginRedirectPath = useMemo(() => postLoginRedirect, [postLoginRedirect]);

    return {
        isFirstPurchase,
        primaryYatRedirect: primaryYatRequired,
        transferRedirect,
        twitterRedirect,
        passwordRequired,
    };
}
